/**
 * ClientsDatabase.tsx — Every client with data in the registry.
 * Rebuilt in Pass 3.
 *
 * This page uses CLIENT-SIDE sort (not server) because the dataset is small.
 * Each row also has an inline "View outlets" action to pivot into the Matched
 * Outlets page filtered to that client.
 */

import { useEffect, useState, useMemo } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import {
  PageHeader,
  Icon,
  SearchInput,
  Table, TableToolbar,
  Tag,
  EmptyState,
  toast,
} from '../../components/ui';

interface ClientRow {
  client_name: string;
  total_records: number;
  matched_records: number;
  unmatched_records: number;
  matched_outlets: number;
  match_rate: number;
  pending_review: number;
  user_count: number;
  first_onboarded: string | null;
  last_pipeline_run: string | null;
}

type SortKey = keyof ClientRow;
type SortDir = 'asc' | 'desc';

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function MatchRateCell({ value }: { value: number }) {
  const kind = value >= 95 ? 'success' : value >= 80 ? 'warning' : 'danger';
  return <Tag kind={kind} mono uppercase={false} size="sm">{value}%</Tag>;
}

export default function ClientsDatabase() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('total_records');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    document.title = 'Clients | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.get<{ clients: ClientRow[] }>('/api/clients/database', { signal: controller.signal })
      .then(res => setClients(res.data.clients || []))
      .catch(err => {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          toast.error('Failed to load clients');
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const sorted = useMemo(() => {
    const filtered = search
      ? clients.filter(c => c.client_name.toLowerCase().includes(search.toLowerCase()))
      : clients;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [clients, search, sortKey, sortDir]);

  // Hand-rolled sort handler — Table component supports sortable columns via its
  // own state, but we need to sync our state for the sorted memo above.
  // Approach: wire column render fns but handle click ourselves via header.
  // For simplicity here we expose sort state via the columns.

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const columns = [
    {
      key: 'client_name',
      header: <SortHeader label="Client" active={sortKey === 'client_name'} dir={sortDir} onClick={() => handleSort('client_name')} />,
      render: (r: ClientRow) => (
        <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.semibold }}>
          {r.client_name}
        </span>
      ),
    },
    {
      key: 'total_records',
      header: <SortHeader label="Records" active={sortKey === 'total_records'} dir={sortDir} onClick={() => handleSort('total_records')} />,
      align: 'right' as const,
      mono: true,
      render: (r: ClientRow) => r.total_records.toLocaleString(),
    },
    {
      key: 'match_rate',
      header: <SortHeader label="Match Rate" active={sortKey === 'match_rate'} dir={sortDir} onClick={() => handleSort('match_rate')} />,
      align: 'right' as const,
      render: (r: ClientRow) => <MatchRateCell value={r.match_rate} />,
    },
    {
      key: 'matched_outlets',
      header: <SortHeader label="Matched Outlets" active={sortKey === 'matched_outlets'} dir={sortDir} onClick={() => handleSort('matched_outlets')} />,
      align: 'right' as const,
      mono: true,
      render: (r: ClientRow) => <span style={{ color: theme.text.secondary }}>{r.matched_outlets.toLocaleString()}</span>,
    },
    {
      key: 'pending_review',
      header: <SortHeader label="Pending" active={sortKey === 'pending_review'} dir={sortDir} onClick={() => handleSort('pending_review')} />,
      align: 'right' as const,
      mono: true,
      render: (r: ClientRow) => (
        <span style={{ color: r.pending_review > 0 ? theme.semantic.warning : theme.text.tertiary }}>
          {r.pending_review.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'user_count',
      header: <SortHeader label="Users" active={sortKey === 'user_count'} dir={sortDir} onClick={() => handleSort('user_count')} />,
      align: 'right' as const,
      mono: true,
      render: (r: ClientRow) => <span style={{ color: theme.text.secondary }}>{r.user_count}</span>,
    },
    {
      key: 'first_onboarded',
      header: <SortHeader label="Onboarded" active={sortKey === 'first_onboarded'} dir={sortDir} onClick={() => handleSort('first_onboarded')} />,
      render: (r: ClientRow) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{formatDate(r.first_onboarded)}</span>,
      nowrap: true,
    },
    {
      key: 'last_pipeline_run',
      header: <SortHeader label="Last Run" active={sortKey === 'last_pipeline_run'} dir={sortDir} onClick={() => handleSort('last_pipeline_run')} />,
      render: (r: ClientRow) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{relativeTime(r.last_pipeline_run)}</span>,
      nowrap: true,
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Clients"
        description="Every client with data in the registry."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {sorted.length.toLocaleString()}
            </span>
            <span>clients</span>
          </>
        }
      />

      <div style={{ display: 'flex', gap: theme.space[3], marginBottom: theme.space[5], flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px', maxWidth: 400 }}>
          <SearchInput
            placeholder="Search by client name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            kbd={undefined}
          />
        </div>
      </div>

      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {sorted.length.toLocaleString()}
          </span>
        </div>
      </TableToolbar>

      <Table
        data={sorted}
        columns={columns}
        loading={loading}
        minWidth={1100}
        rowKey={r => r.client_name}
        emptyState={
          <EmptyState
            icon={<Icon.Users size={18} />}
            title="No clients found"
            description={search ? "Try a different search." : "No clients have loaded data yet."}
          />
        }
      />
    </div>
  );
}

/* ═══════════════ SORT HEADER ═══════════════ */

function SortHeader({
  label, active, dir, onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <span
      onClick={onClick}
      style={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: active ? theme.text.primary : 'inherit',
      }}
    >
      {label}
      {active && (dir === 'asc'
        ? <Icon.ChevronUp size={12} />
        : <Icon.ChevronDown size={12} />
      )}
    </span>
  );
}
